import { Box } from "@mui/material";
import { Fragment, type ReactNode } from "react";
import { useThemeContext } from "../../globalContext/global-context";
import {
  columnGroupSpans,
  rowGroupSections,
  type VisualGroup,
} from "../visualGroups";
import { wideGridColumnHeaderStyle } from "./gridInstrumentStyle";

export const GridRowGroups = <Row,>({
  rows,
  groups,
  rowId,
  columns,
  children,
}: {
  rows: readonly Row[];
  groups?: readonly VisualGroup<string>[];
  rowId: (row: Row, index: number) => string | undefined;
  columns: number;
  children: (row: Row, index: number) => ReactNode;
}) =>
  rowGroupSections(rows, groups, rowId).map((section, index) => (
    <Box component="tbody" key={index}>
      <GroupRowHeading
        groups={groups}
        value={rowId(section.rows[0].row, section.rows[0].index)}
        columns={columns}
      />
      {section.rows.map(({ row, index }) => (
        <Fragment key={index}>{children(row, index)}</Fragment>
      ))}
    </Box>
  ));

export const GroupColumns = <Value extends string | number>({
  values,
  groups,
}: {
  values: readonly Value[];
  groups?: readonly VisualGroup<Value>[];
}) =>
  columnGroupSpans(values, groups ?? []).map((span, index) => (
    <colgroup span={span.count} key={index} />
  ));

export const GroupRowHeading = ({
  groups,
  value,
  columns,
}: {
  groups?: readonly VisualGroup<string>[];
  value?: string;
  columns: number;
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const group = groups?.find((item) => item.values[0] === value);
  return group ? (
    <Box component="tr" data-visual-row-group>
      <Box
        component="th"
        scope="rowgroup"
        colSpan={columns}
        sx={{
          padding: `${theme.tokens.spacing["3"]}px`,
          textAlign: "center",
          fontWeight: theme.tokens.typography.weight.bold,
          color: theme.tokens.color.text.primary,
          backgroundColor: theme.tokens.color.surface.canvas,
          overflowWrap: "anywhere",
        }}
      >
        {group.text}
      </Box>
    </Box>
  ) : null;
};

export const GroupColumnHeadings = <Value extends string | number>({
  values,
  groups,
  trailingColumns = 0,
}: {
  values: readonly Value[];
  groups?: readonly VisualGroup<Value>[];
  trailingColumns?: number;
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  if (!groups?.length) return null;
  return (
    <Box component="tr" data-visual-column-groups>
      <Box component="th" />
      {columnGroupSpans(values, groups).map((span, index) => (
        <Box
          component="th"
          scope="colgroup"
          colSpan={span.count}
          key={index}
          sx={{
            ...wideGridColumnHeaderStyle(theme, true),
            color: theme.tokens.color.text.primary,
            fontWeight: theme.tokens.typography.weight.bold,
          }}
        >
          {span.text}
        </Box>
      ))}
      {trailingColumns > 0 && <Box component="th" colSpan={trailingColumns} />}
    </Box>
  );
};
